import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const CLIENTS = [
  {
    id: "castle",
    name: "Castle Groundcare",
    url: "https://castle-groundcare.emergent.host/",
    trade: "Landscaping & Groundcare",
    location: "Great Dunmow, Essex",
    description: "Nick's 10-year-old landscaping business was running on word-of-mouth and a phone number. ReadyTrades built a full brand, a five-page website and a quote system that positions Castle for higher-value residential and estate contracts across Essex.",
    services: ["Lawn care & maintenance", "Garden design & planting", "Patio & decking", "Hedge trimming & tree work", "Fencing"],
    image: "https://customer-assets.emergentagent.com/job_castle-groundcare/artifacts/2b11p2h6_Screenshot%202026-02-20%20at%2014.35.51.png",
    palette: ["#1F4632", "#5C6C4E", "#D8CDB7", "#F5F1EA"],
    stamp: "Live",
  },
  {
    id: "sh",
    name: "S.H Vehicle Services",
    url: "https://www.shvehicleservices.com",
    trade: "Automotive · Servicing",
    location: "Thaxted, Essex",
    description: "Family-run automotive workshop with over 14 years of experience. ReadyTrades built a clean, mobile-first website and quote system tailored to a dealership-quality car servicing business.",
    services: ["Full service", "MOT preparation", "Diagnostics", "Brake service"],
    image: "https://customer-assets.emergentagent.com/job_729e5acf-67b3-446e-9c65-1f64165cac0a/artifacts/jxwna9w1_IMG_2304.jpg",
    palette: ["#132C3E", "#1F5F82", "#EF5B25", "#EEE7DA"],
    stamp: "Live",
  },
  {
    id: "gm",
    name: "Gilbert & McCarty",
    url: "https://www.gilbertandmccarty.co.uk",
    trade: "Heritage Glazing",
    location: "United Kingdom",
    description: "Specialist makers of Cuprolux (copperlight) glazing — a contemporary take on the iconic 1920s Art Deco system. A quietly premium website suited to heritage architects and conservation projects.",
    services: ["Copperlight glazing", "Cuprolux systems", "Heritage restoration", "Bespoke architectural glazing"],
    image: "https://static.wixstatic.com/media/0564fd_c0e06584583146fbba430ced69622b28~mv2.jpg/v1/fill/w_1200,h_700,al_c,q_85/0564fd_c0e06584583146fbba430ced69622b28~mv2.jpg",
    palette: ["#2A241A", "#8A6D3B", "#C8A96A", "#F5F1EA"],
    stamp: "Live",
  },
];

const OurClientsPage = () => (
  <div className="min-h-screen bg-[color:var(--bg)] overflow-x-hidden" data-testid="our-clients-page">
    <SiteHeader />

    {/* Header */}
    <section className="pt-28 sm:pt-32 lg:pt-40 pb-16 lg:pb-24 border-b border-[color:var(--border-soft)]">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 items-baseline mb-8">
          <div className="col-span-6"><span className="grid-ref">§ Our Work</span></div>
          <div className="col-span-6 flex justify-end"><span className="stamp stamp-ink">Live client projects</span></div>
        </div>
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-8">
            <h1 className="h-xl text-[color:var(--ink)]">
              A small studio, <span className="font-serif-italic">carefully chosen</span> work.
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <p className="copy-lg text-[color:var(--muted)]">
              Live client projects, each with a distinct brand direction. New client work published as it launches.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Cases */}
    {CLIENTS.map((c, idx) => (
      <section
        key={c.id}
        className="py-20 lg:py-32 border-b border-[color:var(--border-soft)]"
        data-testid={`client-${c.id}`}
      >
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-4 mb-10">
            <span className="grid-ref">§ 0{idx + 1}</span>
            <span className="h-px flex-1 bg-[color:var(--border)]" />
            <span className="grid-ref">Case Study {idx + 1} of {CLIENTS.length}</span>
          </div>

          <div className="grid grid-cols-12 gap-8 lg:gap-14 items-start mb-10">
            <div className="col-span-12 lg:col-span-7">
              <span className="grid-ref mb-2 block">Trade — {c.trade}</span>
              <h2 className="h-lg text-[color:var(--ink)]">{c.name}</h2>
              <p className="text-[13px] text-[color:var(--muted)] mt-2">{c.location}</p>
            </div>
            <div className="col-span-12 lg:col-span-5">
              <p className="copy-lg text-[color:var(--muted)]">{c.description}</p>
              <a
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-navy mt-6 inline-flex"
                data-testid={`visit-${c.id}`}
              >
                Visit the live site <ArrowUpRight size={14} />
              </a>
            </div>
          </div>

          {/* Visual grid */}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-8 relative">
              <div className="rounded-sm overflow-hidden aspect-[16/10] bg-[color:var(--bg-alt)] border border-[color:var(--border-soft)]">
                <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="absolute top-4 left-4"><span className="stamp stamp-light">01 · Live site</span></div>
              <div className="absolute bottom-4 right-4"><span className="stamp stamp-light">{c.stamp}</span></div>
            </div>

            <div className="col-span-12 md:col-span-4 grid grid-cols-2 md:grid-cols-1 gap-4">
              <div className="bg-[color:var(--bg-paper)] border border-[color:var(--border-soft)] rounded-sm p-5 md:p-6 relative">
                <span className="grid-ref absolute top-4 left-4">02 · Palette</span>
                <div className="mt-8 grid grid-cols-4 gap-1.5">
                  {c.palette.map((sw) => (
                    <div key={sw} className="aspect-square rounded-sm border border-[color:var(--border-soft)]" style={{ background: sw }} />
                  ))}
                </div>
              </div>
              <div className="bg-[color:var(--ink)] text-white rounded-sm p-5 md:p-6 relative">
                <span className="grid-ref text-white/60 absolute top-4 left-4">03 · Services</span>
                <ul className="mt-8 space-y-1.5 text-[13px]">
                  {c.services.map((s) => (
                    <li key={s} className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[color:var(--accent)] mt-2 flex-shrink-0" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    ))}

    {/* CTA */}
    <section className="py-20 lg:py-32">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 text-center">
        <span className="grid-ref">§ Your business next</span>
        <h2 className="h-lg text-[color:var(--ink)] mt-4 max-w-3xl mx-auto">
          Want to be <span className="font-serif-italic">next up</span> on this page?
        </h2>
        <p className="copy-lg text-[color:var(--muted)] mt-5 max-w-xl mx-auto">
          Tell us about your trade business and we'll design a free preview website — no card, no obligation.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/get-started" className="btn-primary" data-testid="clients-cta">Get My Free Preview <ArrowRight size={15} /></Link>
          <a href="mailto:contact@readytrades.co.uk" className="btn-secondary">Talk to ReadyTrades</a>
        </div>
      </div>
    </section>

    <SiteFooter />
  </div>
);

export default OurClientsPage;
